<script setup lang="ts">
import { defineProps, ref } from "vue";

import aniA1 from "@img/ani_a_1.svg";
import aniB1 from "@img/ani_b_1.svg";
import aniB2 from "@img/ani_b_2.svg";
import aniB3 from "@img/ani_b_3.svg";
import aniB4 from "@img/ani_b_4.svg";
import aniC1 from "@img/ani_c_1.svg";
import aniC2 from "@img/ani_c_2.svg";
import aniC3 from "@img/ani_c_3.svg";
import aniC4 from "@img/ani_c_4.svg";
import aniC5 from "@img/ani_c_5.svg";
import aniC6 from "@img/ani_c_6.svg";

import Modal from "@/components/common/Modal.vue";
import Reviews from "@/components/common/Reviews.vue";
import Block from "@/components/ui/atoms/Block.vue";
import BlockContainer from "@/components/ui/atoms/BlockContainer.vue";
import Btn from "@/components/ui/atoms/Btn.vue";
import MasterBlock from "@/components/ui/atoms/MasterBlock.vue";
import Pill from "@/components/ui/atoms/Pill.vue";
import PillContainer from "@/components/ui/atoms/PillContainer.vue";
import CourseCard from "@/components/ui/molecules/CourseCard.vue";
import Cta from "@/components/ui/molecules/Cta.vue";
import FullVideo from "@/components/ui/molecules/FullVideo.vue";
import PackCard from "@/components/ui/molecules/PackCard.vue";
import ProductPriceCard from "@/components/ui/molecules/ProductPriceCard.vue";
import MainLayout from "@/layouts/MainLayout.vue";
import type { ProductPrice } from "@/shared/checkoutTypes";
import type {
  Course,
  Project,
  Review,
  Specialization,
  SubjectPack,
} from "@/shared/explorerTypes";
import "@/styles/tools/_froala.scss";
import { resolveUrl } from "@/utils/urls";

interface Props {
  lastCourses: Course[];
  topCourses: Course[];
  specializations: Specialization[];
  reviews: Review[];
  projects: Project[];
  productPrices: ProductPrice[];
  packs: SubjectPack[];
}

const props = defineProps<Props>();

const previewVideo = "https://cdn.mastermind.ac/public/mastermind-video-banner-v2.mp4";
const fullScreenVideo = "https://cdn.mastermind.ac/public/mastermind-video-v2.mp4";

const isFullScreenVideo = ref(false);
const currentCourses = ref(props.lastCourses);
const isShowedTop = ref(false);
const showSpecialization = ref(false);
const currentSpecialization = ref<Specialization | null>(null);
const blockContainerRef = ref(null);

const showFullVideo = () => {
  isFullScreenVideo.value = true;
};

const faqList = [
  {
    id: 1,
    question: "¿Qué es Mastermind y qué cursos ofrecemos?",
    answer:
      "<p>Bienvenido a una forma de aprender que no entiende de tiempo o espacio, Mastermind es una escuela de tecnología 100% online donde encontrarás cursos sobre desarrollo de software e informática. Puedes aprender desde programación web hasta hacking ético o montaje y reparación de PC.</p>",
  },
  {
    id: 2,
    question: "¿De qué forma puedes comprar nuestros cursos?",
    answer: `<p>Te ofrecemos varios métodos de compra para que escojas el que mejor se adapta a ti: </p>
      <ul>
        <li><b>Suscripción con acceso a todo el contenido disponible</b>, en tres planes diferentes: manual, trimestral y anual.</li>
        <li><b>Compra el curso que más te guste</b>, por ejemplo, si solo quieres aprender HTML y CSS y no te apetece tener acceso a todo.</li>
        <li><b>Packs de cursos</b>, agrupa varios cursos relacionados y aprende algo concreto, por ejemplo: introducción a la programación.</li>
      </ul>`,
  },
  {
    id: 3,
    question: "¿Qué está incluido en tu compra?",
    answer: `Con cualquiera de las tres opciones anteriores obtienes:
      <ul>
        <li>Acceso al curso, pack o todos los cursos (según lo que compres)</li>
        <li>Ejercicios y proyectos corregidos por nuestros profes</li>
        <li>¿Te ayudamos? Soporte para todas tus dudas en cada clase, tu pregunta  y nosotros la resolvemos.</li>
        <li>Itinerarios guiados (para suscripciones) que te ayudan a no perderte. </li>
        <li>Certificado cuando termines y apruebes el curso/li>
      </ul>`,
  },
  {
    id: 4,
    question: "¿Qué métodos de pago aceptamos?",
    answer:
      "<p>Aceptamos la mayoría de métodos de pago: tarjetas de crédito, débito, prepago y PayPal. Recuerda que el cambio de moneda es automático, aunque el precio esté en euros puedes comprar desde cualquier parte del mundo. ¡Fácil y rápido!</p>",
  },
  {
    id: 5,
    question: "¿Cuándo recibo mi certificado?",
    answer:
      "<p>Una vez que hayas <b>completado el curso al 100%</b> y hayas <b>aprobado</b> el proyecto final, ¡recibirás tu certificado automáticamente! Es ideal para enriquecer tu currículum y mostrar las habilidades aprendidas. Este certificado no es un título oficial, pero sí refleja el esfuerzo y aprendizaje logrado en el campo de estudio que decidas formarte.</p>",
  },
  {
    id: 6,
    question: "¿Qué hago si tengo problemas para acceder a mi curso o a la escuela?",
    answer:
      "<p>¡No te preocupes! Solo escríbenos y te ayudaremos a resolverlo enseguida. Utiliza el chat de la plataforma o escribe a soporte@mastermind.ac. Nuestro horario de atención es de lunes a viernes de 09:00 a 18:00 (UCT+1). </p>",
  },
  {
    id: 7,
    question: "¿Puedo cancelar mi suscripción en cualquier momento?",
    answer:
      "<p>Sí, puedes cancelar cuando quieras, en tu perfil de usuario tienes todo preparado para ser completamente autónomo gestionando tu suscripción. No hay ataduras, aunque esperamos que te quedes con nosotros mucho tiempo.</p>",
  },
  {
    id: 8,
    question: "¿Tienes más dudas?",
    answer: "<p>Escríbenos al chat o al email y las resolveremos encantados.</p>",
  },
];

const openFaqs = ref<number[]>([]);

const toggleFaq = (id: number) => {
  const index = openFaqs.value.indexOf(id);
  if (index === -1) {
    openFaqs.value.push(id);
    return;
  }
  openFaqs.value.splice(index, 1);
};

const changeSpecialization = (specialization: Specialization) => {
  showSpecialization.value = true;
  currentSpecialization.value = specialization;
};
</script>

<template>
  <MainLayout>
    <MasterBlock theme="off-black" :class="['video-banner-container']">
      <div class="video-banner">
        <video :src="previewVideo" class="video-class" autoplay loop muted playsinline />
        <div class="content-overlay container">
          <h1>The challenging tech school</h1>
          <h2>Aprende tecnología a tu ritmo, desde cero, haciendo proyectos.</h2>
          <Cta text="Reproducir Video" @click="showFullVideo" />
        </div>
        <div class="video-overlay" />
      </div>
      <FullVideo
        :is-visible="isFullScreenVideo"
        :src="fullScreenVideo"
        @close="isFullScreenVideo = false"
      />
    </MasterBlock>

    <MasterBlock theme="iron">
      <div class="container">
        <div class="container__header">
          <PillContainer theme="dark" shape="round" relaxed>
            <Btn
              id="questionAnswersBtn"
              shape="round"
              theme="iron-bold"
              :class="[{ 'pill--active-bold': !isShowedTop }]"
              @click="
                () => {
                  currentCourses = props.lastCourses;
                  isShowedTop = false;
                  blockContainerRef.resetScroll();
                }
              "
            >
              Últimos cursos
            </Btn>
            <Btn
              shape="round"
              theme="iron-bold"
              :class="[{ 'pill--active-bold': isShowedTop }]"
              @click="
                () => {
                  currentCourses = props.topCourses;
                  isShowedTop = true;
                  blockContainerRef.resetScroll();
                }
              "
            >
              Top Valorados
            </Btn>
          </PillContainer>
        </div>
        <BlockContainer behaviour="scroll" theme="iron" ref="blockContainerRef">
          <CourseCard
            v-for="course in currentCourses"
            :key="course.id"
            :course="course"
            light
          />
          <Block theme="smoke" tag="article" class="subject-more">
            <p class="size-xl no-margin-bottom">¿BUSCAS ALGO EN CONCRETO?</p>
            <Btn
              tag="a"
              :href="resolveUrl('course_list')"
              shape="round"
              theme="active-bold"
              class="sizeplus"
            >
              Ver todos los cursos
            </Btn>
          </Block>
        </BlockContainer>
      </div>
    </MasterBlock>

    <MasterBlock theme="off-black" :class="['tree-explorer']">
      <div class="container">
        <Pill theme="dark" :class="['tree-explorer__heading']">
          <span class="size-xl ff-apparat-medium">Nuestro árbol de habilidades</span>
        </Pill>
        <div class="tree-explorer__img">
          <img src="@img/tree.svg" alt="Explorador de cursos" />
        </div>
        <Block theme="off-white" :class="['tree-explorer__card']">
          <p class="no-margin-top">
            Es como un árbol real, pero en lugar de crecer hojas y ramas, crecen
            habilidades.
          </p>
          <p>
            Empiezas por el tronco, que son las
            <strong>habilidades</strong>
            más básicas, como programación desde cero. Desde ahí, las ramas son
            <strong>diferentes caminos</strong>
            que puedes seguir para desarrollarte en algo más avanzado.
          </p>
          <Btn
            tag="a"
            :href="resolveUrl('tree_show', 'desarrollo')"
            theme="active-bold"
            shape="round"
          >
            Ver el ARBOL
          </Btn>
        </Block>
      </div>
    </MasterBlock>

    <MasterBlock theme="smoke" v-if="specializations.length > 0">
      <div class="container container--padding">
        <div class="container__header">
          <h2 id="especializaciones">Aprende una profesión</h2>
        </div>
        <BlockContainer theme="smoke" behaviour="scroll" class="specialization-container">
          <Block
            v-for="specialization in specializations"
            :key="specialization.id"
            :class="['specialization']"
            @click="changeSpecialization(specialization)"
          >
            <div class="specialization__img">
              <img :src="specialization.image" alt="Specialization" />
            </div>
            <div class="specialization__info">
              <h3 v-html="specialization.name" class="no-margin-bottom" />
            </div>
          </Block>
        </BlockContainer>
      </div>
      <Modal
        v-if="currentSpecialization"
        :title="`Camino profesional: ${currentSpecialization.name}`"
        :show="showSpecialization"
        @close="showSpecialization = false"
        class="specialization-modal"
      >
        <template #body>
          <div class="modal-content">
            <Block theme="off-black" class="spec-image">
              <img :src="currentSpecialization.image" alt="Specialization" />
            </Block>
            <div class="spec-info">
              <div class="spec-info__text">
                <div v-html="currentSpecialization.description" class="froala-content" />
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <PillContainer full-width on-right class="show-spec">
            <Btn
              tag="a"
              :href="
                resolveUrl(
                  'specialization_show',
                  currentSpecialization.branch_slug,
                  currentSpecialization.slug,
                )
              "
              shape="round"
              theme="dark-bold"
            >
              Ver especialización
            </Btn>
          </PillContainer>
        </template>
      </Modal>
    </MasterBlock>

    <MasterBlock theme="iron" v-if="false">
      <div class="container">
        <div class="container__header">
          <h2>Nuestro método de aprendizaje</h2>
        </div>
        <BlockContainer theme="iron" class="method_item">
          <Block theme="off-white">
            <div class="item">
              <div id="ani_a">
                <object id="ani_a_1" type="image/svg+xml" :data="aniA1" />
              </div>
            </div>
            <h3>1. Aprender conceptos</h3>
            <span>clave, entenderlos de la forma más fácil</span>
          </Block>
          <Block theme="off-white" class="method_item">
            <div class="item">
              <div id="ani_b">
                <object id="ani_b_3" type="image/svg+xml" :data="aniB3" />
                <object id="ani_b_1" type="image/svg+xml" :data="aniB1" />
                <!-- Pistola -->
                <object id="ani_b_2" type="image/svg+xml" :data="aniB2" />
                <!-- Rayo -->
                <object id="ani_b_4" type="image/svg+xml" :data="aniB4" />
              </div>
            </div>
            <h3>2. Practicarlos con ejercicios</h3>
            <span>que te permitan comprenderlos</span>
          </Block>
          <Block theme="off-white" class="method_item">
            <div class="item">
              <div id="ani_c">
                <!-- UFO izquierda -->
                <object id="ani_c_4" type="image/svg+xml" :data="aniC4" />
                <!-- Rayo izquierda -->
                <object id="ani_c_6" type="image/svg+xml" :data="aniC6" />
                <!-- Ciudad -->
                <object id="ani_c_1" type="image/svg+xml" :data="aniC1" />
                <!-- Rayo derecha -->
                <object id="ani_c_2" type="image/svg+xml" :data="aniC2" />
                <!-- UFO derecha -->
                <object id="ani_c_3" type="image/svg+xml" :data="aniC3" />
                <!-- Cajas -->
                <object id="ani_c_5" type="image/svg+xml" :data="aniC5" />
              </div>
            </div>
            <h3>3. Crear aplicaciones y proyectos</h3>
            <span>donde verás como se aplican en el mundo real</span>
          </Block>
        </BlockContainer>
      </div>
    </MasterBlock>

    <MasterBlock theme="dust">
      <div class="container">
        <div class="container__header" id="certificate-header">
          <h2 class="ff-apparat-medium">Aprende, Practica,</h2>
          <Pill theme="active" wireframe>
            <span class="ff-apparat-medium">Certifícate</span>
          </Pill>
        </div>

        <div class="certificate">
          <div class="certificate__img">
            <img src="@img/certificate.svg" alt="Certificado" />
          </div>
          <div class="certificate__text">
            <h2 class="size-xl">DE MAESTROS EXPERIMENTADOS, APRENDICES AVENTAJADOS</h2>
            <p>
              Como escuela especializada, entendemos que el aprendizaje no termina en las
              clases. Nos enfocamos en brindarte las habilidades prácticas que necesitas
              para destacarte en el mundo laboral y para transformar tus pasiones en
              logros. Aquí, cada proyecto es una oportunidad de mostrar tu talento y
              construir un futuro prometedor, por eso te entregamos un certificado que
              demuestra todo lo aprendido y el trabajo hecho en cada curso.
            </p>
          </div>
        </div>
      </div>
    </MasterBlock>

    <MasterBlock theme="off-white">
      <section class="container">
        <div class="container__header">
          <h2>Soporte siempre y cuando lo necesites</h2>
        </div>
        <div class="perks-container">
          <BlockContainer
            behaviour="stripes"
            theme="off-white"
            class="perks-container__row"
          >
            <Block class="perk-item">
              <img src="@img/ejercicios.png" alt="Acceso total" />
              <div class="perk-item__text">
                <h3 class="size-l">EJERCICIOS CORREGIDOS POR PROFESORES</h3>
                <p class="no-margin-bottom">
                  Recibe retroalimentación real en cada ejercicio, revisado por nuestros
                  expertos docentes.
                </p>
              </div>
            </Block>
            <Block class="perk-item">
              <img src="@img/foro-de-soporte.png" alt="Acceso total" />
              <div class="perk-item__text">
                <h3 class="size-l">Resolvemos tus dudas en cada clase</h3>
                <p class="no-margin-bottom">
                  Comparte tus dudas y obtén respuestas de nuestros expertos y de la
                  comunidad.
                </p>
              </div>
            </Block>
            <Block class="perk-item">
              <img
                src="../assets/img/actualizaciones-constantes.png"
                alt="Acceso total"
              />
              <div class="perk-item__text">
                <h3 class="size-l">ACTUALIZACIONES FRECUENTES DE CONTENIDO</h3>
                <p class="no-margin-bottom">
                  Revisamos y mejoramos el contenido de forma constante para estar al día.
                </p>
              </div>
            </Block>
          </BlockContainer>
        </div>
      </section>
    </MasterBlock>

    <MasterBlock theme="off-black" :class="['pricing']">
      <div class="container">
        <div class="container__header">
          <h2>Suscríbete y accede a todo nuestro contenido</h2>
        </div>
        <BlockContainer behaviour="stripes" :class="['pricing-container']">
          <ProductPriceCard
            v-for="productPrice in productPrices"
            :productPrice="productPrice"
            :class="['price-card']"
            :key="productPrice.id"
          />
        </BlockContainer>
        <div class="pricing__perks">
          <h3>¿QUÉ INCLUYE LA SUSCRIPCIÓN?</h3>
          <ul>
            <li>Acceso a más de 70 cursos sobre tecnología</li>
            <li>Ejercicios y proyectos corregidos por profesores reales</li>
            <li>Soporte personalizado por parte de nuestros profesores</li>
            <li>Itinerario de aprendizaje guiado</li>
            <li>
              Acceso a foro privado en cada curso para resolver dudas y compartir
              experiencias
            </li>
            <li>Certificados por superar cada curso</li>
          </ul>
        </div>
      </div>
    </MasterBlock>

    <MasterBlock theme="off-white" :class="['packs']" v-if="packs.length > 0">
      <section class="container">
        <div class="container__header">
          <h2>¿Estás interesado en algo específico?</h2>
        </div>
        <BlockContainer theme="off-white">
          <PackCard :pack="pack" v-for="pack in packs" :key="pack.id" />
          <Block theme="dust" class="packs__more">
            <h3>¿No has encontrado lo que buscas?</h3>
            <p>Accede a nuestro catálogo completo</p>
            <Btn
              tag="a"
              :href="resolveUrl('pack_list')"
              theme="active-bold"
              shape="round"
              class="sizeplus"
            >
              Explorar nuestros packs
            </Btn>
          </Block>
        </BlockContainer>
        <div class="packs__cta">
          <Btn
            tag="a"
            :href="resolveUrl('pack_list')"
            theme="active-bold"
            shape="round"
            class="sizeplus"
          >
            Explora nuestros packs
          </Btn>
        </div>
      </section>
    </MasterBlock>

    <MasterBlock theme="shadow">
      <div class="container" id="preguntas">
        <div class="container__header">
          <h2>Preguntas frecuentes</h2>
        </div>
        <Block theme="clear">
          <Block
            @click="toggleFaq(faq.id)"
            theme="off-white"
            class="faq-container"
            v-for="faq in faqList"
            :key="faq.id"
          >
            <div class="faq__question">
              <h4>{{ faq.question }}</h4>
              <span v-if="!openFaqs.includes(faq.id)">
                <img src="@img/icons/collapse-open.svg" alt="Capítulo expandido" />
              </span>
              <span v-else>
                <img src="@img/icons/collapse-close.svg" alt="Capítulo cerrado" />
              </span>
            </div>
            <div class="faq__answer" v-if="openFaqs.includes(faq.id)">
              <div v-html="faq.answer" />
            </div>
          </Block>
        </Block>
      </div>
    </MasterBlock>

    <MasterBlock theme="off-white">
      <section class="container">
        <div class="container__header">
          <h2>¿QUE DICEN NUESTROS ALUMNOS?</h2>
        </div>
        <Reviews :reviews="reviews" show-subject />
      </section>
    </MasterBlock>
  </MainLayout>
</template>

<style scoped lang="scss">
@use "@/styles/settings/vars";

.video-banner-container {
  height: calc(90vh - 60px);
  overflow: hidden;
}

.video-banner {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  position: relative;
}

.video-class {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.container {
  margin: 0 auto;
  position: relative;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 5;
}

@media only screen and (max-width: 768px) {
  .video-banner-container {
    height: calc(80vh - 60px);
  }
}

.content-overlay {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
  z-index: 10;
  padding: 0 1em;

  h1,
  h2 {
    max-width: 800px;
  }

  h2 {
    text-transform: none;
  }
}

.switch-courses {
  margin-bottom: 2em;
}

.subject-more {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1em;
  min-width: 600px;
  max-width: 800px;

  @media (max-width: 600px) {
    min-width: 85%;
    max-width: 85%;
    text-align: center;
    p {
      margin-bottom: 0.5em;
    }
  }

  p {
    font-weight: bold;
    text-transform: uppercase;
  }
}

.specialization-container {
  .specialization {
    display: flex;
    flex-direction: column;
    min-width: 25%;
    justify-content: space-between;
    background-color: #1b1d22;
    gap: 1em;
    cursor: pointer;

    @media (width < 600px) {
      max-width: 100%;
      min-width: 70%;
    }

    p {
      margin-bottom: 0;
    }

    .specialization__img {
      display: flex;
      align-items: center;
      width: 100%;
      height: 200px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .specialization__info {
      h3 {
        text-transform: uppercase;
        overflow-wrap: break-word;
      }

      p {
        color: vars.$smoke;
      }
    }
  }
}

.tree-explorer {
  background-image: url("../assets/img/tree-background.svg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (width < 600px) {
  }

  &__heading {
    position: absolute;
    top: 2em;

    @media (width < 600px) {
      top: 0;
      position: inherit;
      width: inherit;
    }
  }

  &__img {
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 110%;
      height: auto;

      @media (width < 1024px) {
        width: 100%;
      }
    }
  }

  &__card {
    position: absolute;
    max-width: 280px;
    margin-bottom: 0;
    bottom: 2em;

    @media (width < 1024px) {
      bottom: 0;
      position: inherit;
    }
  }
}

.perks-container {
  .perk-item {
    &__text {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      h3 {
        flex-grow: 1;
        text-transform: uppercase;
      }

      p {
        margin: 0;
        align-self: flex-end;
      }
    }

    img {
      max-width: 100%;
      margin: 0 auto 1em;
    }
  }
}

#certificate-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  h2 {
    margin-bottom: 0;
    font-size: 44px;

    @media (max-width: 1024px) {
      font-size: 32px;
    }

    @media (max-width: 768px) {
      font-size: 24px;
    }

    @media (max-width: 600px) {
      font-size: 20px;
    }
  }

  .pill {
    padding: 0.5em;

    span {
      font-size: 44px;
      line-height: 1;

      @media (max-width: 1024px) {
        font-size: 32px;
      }

      @media (max-width: 768px) {
        font-size: 24px;
      }

      @media (max-width: 600px) {
        font-size: 20px;
      }
    }
  }
}

.certificate {
  padding-top: 1em;
  display: grid;
  grid-template-columns: 2.5fr 1fr;
  gap: 1em;

  @media (width < 1024px) {
    grid-template-columns: 1fr;
  }

  &__img {
    img {
      width: 100%;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    justify-content: center;

    h2 {
      margin-bottom: 0;
    }

    p {
      margin-bottom: 0;
    }
  }
}

.faq-container {
  .faq__question {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    h4 {
      margin: 0;
      text-transform: uppercase;
    }

    img {
      width: 20px;
      height: 20px;
    }
  }

  .faq__answer {
    padding-top: 1em;

    :deep(ul) {
      margin-bottom: 0;
    }

    :deep(li:not(:last-child)) {
      margin-bottom: 0.5em;
    }

    :deep(p:first-child) {
      margin-top: 0;
    }

    :deep(p:last-child) {
      margin-bottom: 0;
    }
  }
}

.pricing {
  &__perks {
    margin-top: 2em;

    ul {
      margin-bottom: 0;
    }
  }
}

.packs {
  &__more {
    display: flex;
    flex-direction: column;
    justify-content: center;

    h3 {
      margin-bottom: 0;
    }

    .btn-action--generic {
      margin-top: 1em;
    }
  }

  &__cta {
    display: flex;
    justify-content: flex-end;
    margin-top: 2em;
  }
}

.specialization-modal {
  display: flex;
  flex-direction: column;

  .modal-content {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 1em;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
      gap: 1em;
    }

    .spec-image {
      display: flex;
      justify-content: center;
      align-items: center;
      height: max-content;

      img {
        display: flex;
        align-items: center;
        width: 100%;
        height: 240px;
        overflow: hidden;
      }
    }

    .spec-info {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
}

.method_item {
  h3 {
    margin: 2em 0 0 0;
  }
}

// Animation

/* Nueva clase .item para agrupar cada div y su texto */
.item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Estilos comunes para ani_a, ani_b y ani_c */
#ani_a,
#ani_b,
#ani_c {
  position: relative;
  width: 300px; /* Mismo ancho para todos */
  height: 450px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Estilos específicos para ani_a */
#ani_a_1 {
  animation: rotateSlow 10s ease-in-out infinite alternate;
  width: 70%;
}

/* Animación para ani_a */
@keyframes rotateSlow {
  0% {
    transform: rotate(0deg) translateY(0);
  }
  50% {
    transform: rotate(6deg) translateY(-20px);
  }
  100% {
    transform: rotate(-15deg) translateY(0);
  }
}

/* Estilos comunes para los objetos dentro de ani_b y ani_c */
#ani_b object,
#ani_c object {
  position: absolute;
}

/* Estilos específicos para ani_b */

/* Posiciones y tamaños para elementos de ani_b */
#ani_b_1 {
  width: 50%;
  top: 0;
  left: 51px;
  transform-origin: top left;
  animation: rotacionBrazo 4s ease-in-out alternate infinite;
}

#ani_b_2 {
  width: 35%;
  top: 240px;
  left: 190px;
  animation: flotacionSutil 4s alternate infinite;
}

#ani_b_3 {
  width: 60%;
  top: 90px;
  left: 0;
  transform-origin: top right;
  animation: rotacionCaja 4s ease-in-out alternate infinite;
}

#ani_b_4 {
  width: 15%;
  top: 212px;
  left: 148px;
  opacity: 0;
  animation: fadeInAnimation 4s alternate infinite;
}

/* Animaciones para ani_b */
@keyframes flotacionSutil {
  0% {
    transform: translate(0, 0);
  }
  20% {
    transform: translate(2px, -2px);
  }
  60% {
    transform: translate(-2px, 1px);
  }
  80%,
  100% {
    transform: translate(0, 0);
  }
}

@keyframes fadeInAnimation {
  0%,
  80% {
    opacity: 0;
  }
  85%,
  100% {
    opacity: 1;
  }
}

@keyframes rotacionCaja {
  0% {
    transform: rotate(-4deg) translateY(-30px) translateX(19px);
  }
  75%,
  100% {
    transform: rotate(-19deg) translateY(-6px) translateX(0px);
  }
}

@keyframes rotacionBrazo {
  0% {
    transform: rotate(-10deg);
  }
  75%,
  100% {
    transform: rotate(2deg);
  }
}

/* Posiciones y tamaños para elementos de ani_c */
#ani_c_1 {
  width: 50%; /* La ciudad ocupa la mitad del ancho */
  top: 0;
  left: 30%;
}

#ani_c_2 {
  width: 35%;
  top: 217px;
  left: 138px;
  transform-origin: top right;
  animation: ufoFloatRay 7s ease-in-out infinite;
}

#ani_c_3 {
  width: 15%;
  top: 200px;
  left: 220px;
  animation: ufoFloat 7s ease-in-out infinite;
}

#ani_c_4 {
  width: 7%;
  top: 50px;
  left: 70px;
  animation: ufoFloatReverse 5s ease-in-out infinite;
}

#ani_c_5 {
  width: 60%;
  top: 295px;
  left: 20px;
}

#ani_c_6 {
  width: 16%;
  top: 60px;
  left: 81px;
  opacity: 1;
  transform-origin: top left;
  animation: ufoFloatReverseRay 5s ease-in-out infinite;
}

/* Animaciones para ani_c */
@keyframes ufoFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

@keyframes ufoFloatReverse {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(15px);
  }
}

@keyframes ufoFloatRay {
  0%,
  100% {
    transform: translateY(0) rotate(-10deg);
  }
  0%,
  10% {
    opacity: 0;
  }
  20%,
  80% {
    opacity: 1;
  }
  90%,
  100% {
    opacity: 0;
  }
  50% {
    transform: translateY(-15px);
  }
}

@keyframes ufoFloatReverseRay {
  0%,
  100% {
    transform: translateY(0) rotate(-10deg);
  }
  0%,
  10% {
    opacity: 0;
  }
  20%,
  80% {
    opacity: 1;
  }
  90%,
  100% {
    opacity: 0;
  }
  50% {
    transform: translateY(15px);
  }
}

@keyframes rayFadeIn {
  0%,
  70% {
    opacity: 0;
  }
  75%,
  100% {
    opacity: 1;
  }
}
</style>
